/**
 * Receipt processing helpers — image compression + size guard before uploading
 * a file to Supabase Storage. The compressed JPEG is what gets uploaded.
 */

export function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error || new Error("Read failed"));
    r.readAsDataURL(file);
  });
}

async function compressImageToDataURL(file: File, maxDim = 1600, quality = 0.85): Promise<string> {
  const src = await fileToDataURL(file);
  const img = new Image();
  await new Promise<void>((res, rej) => {
    img.onload = () => res();
    img.onerror = () => rej(new Error("Image decode failed"));
    img.src = src;
  });
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const ratio = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d")!.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

export interface ProcessedReceipt {
  dataUrl: string;
  name: string;
  type: string;
}

/** Validate size + return a base64 data URL (compressed for images). */
export async function processReceiptFile(file: File): Promise<ProcessedReceipt> {
  if (file.size > 10 * 1024 * 1024) throw new Error("File is over 10 MB — please use a smaller scan.");
  let dataUrl: string;
  if (file.type.startsWith("image/")) {
    dataUrl = await compressImageToDataURL(file);
  } else {
    dataUrl = await fileToDataURL(file);
  }
  return { dataUrl, name: file.name, type: file.type || "application/octet-stream" };
}
